import { faPencil, faPlus, faRemove, faTag } from '@fortawesome/pro-regular-svg-icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { Divider } from 'src/components/Divider';
import { Icon } from 'src/components/Icon';
import { Skeleton } from 'src/components/Skeleton';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { UserSelectorChip } from 'src/pages/Clients/PaymentDialog/UserSelectorChip';
import { formatCurrency } from 'src/utils/numberFormatter';

import { usePosContext } from '../PosContext';

import { AddDiscoutDialog } from './AddDiscoutDialog';
import CartSummaryRow from './CartSummayRow';
import PaymentMethodsDialog from './PaymentMenthodsDialog';
import { PaymentMethodSelector } from './PaymentMethodSelector';
import { PosCartCard } from './PosCartCard';
import SummaryDialog from './SummaryDialog';

export const PosCart = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pointOfSale.cart',
  });

  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [discountsAccordionOpen, setDiscountsAccordionOpen] = useState(false);
  const [taxesAccordionOpen, setTaxesAccordionOpen] = useState(false);

  const {
    totalPrice,
    removeFromCart,
    cart,
    updateQuantity,
    isLoadingIntent,
    selectedUser,
    setShowWelcomeDialog,
    paymentMethod,
    handleChangePaymentMethod,
    paymentMethodsDialogOpen,
    setPaymentMethodsDialogOpen,
    addDiscountDialogOpen,
    setAddDiscountDialogOpen,
    charge,
    isGuestCheckout,
    guestCheckoutEnabled,
    setShowFindAccountDialog,
    shouldDisableCheckout,
    cartSummary,
    removeDiscount,
  } = usePosContext();

  const handleChangeUser = () => {
    if (!guestCheckoutEnabled) {
      setShowFindAccountDialog(true);
    } else {
      setShowWelcomeDialog(true);
    }
  };

  const { subtotal, tax, taxes, total, accountBalance, discount, discounts } =
    useMemo(() => {
      return {
        subtotal: formatCurrency(cartSummary.subtotal, {
          cents: true,
          currency: cartSummary.currency,
        }),
        tax: formatCurrency(cartSummary.tax, {
          cents: true,
          currency: cartSummary.currency,
        }),
        taxes: Array.from(cartSummary.taxes).map(([key, value]) => ({
          label: key,
          value: formatCurrency(value, {
            cents: true,
            currency: cartSummary.currency,
          }),
        })),
        total: formatCurrency(cartSummary.total, {
          cents: true,
          currency: cartSummary.currency,
        }),
        accountBalance: formatCurrency(cartSummary.accountBalance, {
          cents: true,
          currency: cartSummary.currency,
        }),
        discount: formatCurrency(cartSummary.discount, {
          cents: true,
          currency: cartSummary.currency,
        }),
        discounts: Array.from(cartSummary.discounts).map(([key, value]) => ({
          couponCode: key,
          value: formatCurrency(value, {
            cents: true,
            currency: cartSummary.currency,
          }),
        })),
      };
    }, [cartSummary]);

  return (
    <>
      {/* Larger than md */}
      <Stack
        justifyContent="space-between"
        alignItems="center"
        sx={{
          flex: 1,
          height: '100%',
          bgcolor: 'background.level2',
          borderRadius: 2,
          display: {
            xs: 'none',
            md: 'flex',
          },
        }}
      >
        <Stack
          p={2}
          sx={{
            width: '100%',
          }}
          alignItems="flex-start"
          justifyContent="flex-start"
        >
          <UserSelectorChip
            user={selectedUser}
            onChangeUser={handleChangeUser}
            sx={{
              mb: 0,
              p: 0,
            }}
            endAdornment={
              <Button
                variant="text"
                size="small"
                sx={{
                  '& .MuiButton-endIcon': {
                    fontSize: '12px',
                  },
                  '& .MuiButton-endIcon > *': {
                    fontSize: '12px !important',
                  },
                }}
                endIcon={
                  <Icon icon={faPencil} />
                }
              >
                {t('selectAccount')}
              </Button>
            }
          />
        </Stack>

        <Stack
          p={2}
          spacing={1}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            overflowY: 'scroll',
            scrollbarWidth: 'none',
          }}
        >
          {cart?.cartItems.map((item, index) => (
            <PosCartCard
              key={item.id?.toString() || index}
              item={item}
              removeFromCart={removeFromCart}
              updateQuantity={updateQuantity}
              cartItemIndex={index}
            />
          ))}
        </Stack>

        <Stack
          sx={{
            p: 2,
            pt: 0,
            width: '100%',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {totalPrice > 0 && (
              <Button
                onClick={() => setAddDiscountDialogOpen(true)}
                endIcon={<Icon icon={faPlus} size="xsmall" />}
                sx={{
                  '& .MuiButton-endIcon': {
                    fontSize: '14px',
                  },
                  '& .MuiButton-endIcon > *': {
                    fontSize: '14px !important',
                  },
                }}
              >
                {t('addDiscount')}
              </Button>
            )}
          </Stack>

          <Card
            sx={{ bgcolor: 'background.paper', mb: 1, mt: 0, borderRadius: 1 }}
          >
            <Stack spacing={1} sx={{ p: 2 }}>
              <PaymentMethodSelector
                isGuestCheckout={isGuestCheckout}
                paymentMethod={paymentMethod}
                onPaymentMethodClick={() => setPaymentMethodsDialogOpen(true)}
              />

              <CartSummaryRow
                label={t('subtotal')}
                value={subtotal}
                isLoading={isLoadingIntent}
              />

              {discounts.length > 0 && (
                <>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Button
                      variant="text"
                      sx={{ p: 0, m: 0 }}
                      onClick={() =>
                        setDiscountsAccordionOpen(!discountsAccordionOpen)
                      }
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          textDecoration: 'underline',
                        }}
                      >
                        {t('discounts')}
                      </Typography>
                    </Button>
                    {isLoadingIntent ? (
                      <Skeleton variant="text" width={56} height={18} />
                    ) : (
                      <Typography variant="h6">{discount}</Typography>
                    )}
                  </Stack>

                  {discountsAccordionOpen && (
                    <Stack>
                      {discounts.map((item) => (
                        <Stack
                          key={item.couponCode}
                          ml={1}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Icon
                              size="small"
                              color="info"
                              icon={faTag}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {item.couponCode}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              p={0}
                              m={0}
                            >
                              {item.value}
                            </Typography>
                            <Button
                              variant="text"
                              size="small"
                              sx={{ p: 0, m: 0 }}
                              onClick={() => removeDiscount(item.couponCode)}
                            >
                              <Icon
                                icon={faRemove}
                                size="xsmall"
                              />
                            </Button>
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </>
              )}

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Button
                  variant="text"
                  sx={{ p: 0, m: 0 }}
                  onClick={() => setTaxesAccordionOpen(!taxesAccordionOpen)}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      textDecoration: taxes.length > 0 ? 'underline' : 'none',
                    }}
                  >
                    {t('taxes')}
                  </Typography>
                </Button>
                {isLoadingIntent ? (
                  <Skeleton variant="text" width={56} height={18} />
                ) : (
                  <Typography variant="h6">{tax}</Typography>
                )}
              </Stack>

              {taxesAccordionOpen && (
                <Stack>
                  {taxes.map((item) => (
                    <Stack
                      key={item.label}
                      ml={1}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Icon
                          size="small"
                          color="info"
                          icon={faTag}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {item.label}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          p={0}
                          m={0}
                        >
                          {item.value}
                        </Typography>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}

              {cartSummary.accountBalance !== 0 && (
                <CartSummaryRow
                  label={t('accountBalance')}
                  value={accountBalance}
                  isLoading={isLoadingIntent}
                />
              )}

              <Divider />

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h6">{t('total')}</Typography>
                <Typography variant="h6">
                  {isLoadingIntent ? (
                    <Skeleton variant="text" width={56} height={18} />
                  ) : (
                    total
                  )}
                </Typography>
              </Stack>
            </Stack>
          </Card>

          <Button
            variant="contained"
            disabled={shouldDisableCheckout}
            onClick={charge}
            sx={{
              height: '40px',
              width: {
                sm: 'auto',
                md: '100%',
              },
            }}
          >
            <Typography variant="button">{t('checkout')}</Typography>
          </Button>
        </Stack>
      </Stack>
      <Stack
        sx={{
          display: {
            xs: 'flex',
            md: 'none',
          },
          bgcolor: 'background.level2',
          borderRadius: 2,
          p: 2,
          width: '100%',
        }}
        spacing={2}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <UserSelectorChip
            user={selectedUser}
            onChangeUser={handleChangeUser}
            sx={{
              mb: 0,
              mr: 1,
              p: 0.5,
            }}
            endAdornment={
              <Button
                variant="text"
                size="small"
                sx={{
                  '& .MuiButton-endIcon': {
                    fontSize: '12px',
                  },
                  '& .MuiButton-endIcon > *': {
                    fontSize: '12px !important',
                  },
                }}
                endIcon={
                  <Icon icon={faPencil} />
                }
              >
                {t('selectAccount')}
              </Button>
            }
          />
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'nowrap',
              width: '100%',
            }}
          >
            {t('bag')}
          </Typography>
        </Stack>

        <Button
          variant="contained"
          fullWidth
          sx={{ height: 40 }}
          onClick={() => setSummaryDialogOpen(true)}
        >
          <Typography variant="button">{t('checkout')}</Typography>
        </Button>
      </Stack>
      <AddDiscoutDialog
        open={addDiscountDialogOpen}
        onClose={() => setAddDiscountDialogOpen(false)}
      />
      <SummaryDialog
        open={summaryDialogOpen}
        onClose={() => setSummaryDialogOpen(false)}
      />
      <PaymentMethodsDialog
        open={paymentMethodsDialogOpen}
        onClose={() => setPaymentMethodsDialogOpen(false)}
        user={selectedUser}
        setPaymentMethod={handleChangePaymentMethod}
      />
    </>
  );
};
